import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bed, Bath, Send, Minus, Loader2, ShoppingCart, Trash, Eye, Calendar, Droplets } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { fetchHospedajeById } from '@/services/hospedajes.service';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useAuth } from '@/contexts/AuthContext';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';
import EliminarSolicitudModal from '@/components/solicitudes/EliminarSolicitudModal';

const formatDateForInput = (date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().split('T')[0];
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const getTomorrowDateString = () => formatDateForInput(addDays(new Date(), 1));

// Opciones de justificación para entregas del mismo día
const JUSTIFICACIONES_MISMO_DIA = [
  'Salida anticipada del huésped',
  'Cambio de habitación por inconformidad',
  'Persona adicional no registrada',
  'Cobija adicional por talla del huésped',
  'Toallas mojadas por limpieza de derrame',
  'Toallas mojadas por uso de jacuzzi',
  'Lencería solicitadas en turno nocturno y cargadas en App',
  'Lencería dañada o muy sucia',
  'Pieza faltante en la dotación',
  'Lencería en mal estado detectada por camarera',
  'Check-in anticipado requiere preparación',
  'Huésped solicita cobija extra por frío',
  'Colchón adicional requiere lencería',
  'Cambio por alergias del huésped',
  'Unidad vacante requiere limpieza inmediata'
];

const SolicitarLenceriaLimpia = () => {
  const { hospedajeId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [hospedaje, setHospedaje] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Datos de camas y baños con sus prendas
  const [camasData, setCamasData] = useState([]);
  const [banosData, setBanosData] = useState([]);

  // Items seleccionados para la solicitud
  const [selectedItems, setSelectedItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [fechaEntrega, setFechaEntrega] = useState(() => getTomorrowDateString());
  const [justificacionMismoDia, setJustificacionMismoDia] = useState('');
  const [ocupacion, setOcupacion] = useState(1);

  // Estados para modal de detalle
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  
  // Estados para modal de eliminar
  const [solicitudToDelete, setSolicitudToDelete] = useState(null);
  const [isEliminarModalOpen, setIsEliminarModalOpen] = useState(false);

  // Estados para la tabla de solicitudes
  const [solicitudes, setSolicitudes] = useState([]);
  const [isLoadingSolicitudes, setIsLoadingSolicitudes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const { user } = useAuth();

  // Columnas de la tabla de solicitudes
  const columns = [
    {
      id: 'createdAt',
      label: 'Fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'fechaEntrega',
      label: 'Fecha de entrega',
      sortable: true,
      render: (row) => new Date(row.fechaEntrega).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },
        {
      id: 'edificio',
      label: 'Portafolio',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre}</p>
          <p className="text-xs text-gray-500">{row.edificio?.ciudad}</p>
        </div>
      )
    },
    {
      id: 'nombrePropiedad',
      label: 'Propiedad',
      sortable: true,
      render: (row) => row.nombrePropiedad || row.hospedaje?.nombre || '-'
    },
    {
      id: 'isExtraRuta',
      label: 'Tipo de ruta',
      sortable: true,
      render: (row) => row.isExtraRuta ? (
        <p className="text-xs text-red-600 font-medium"> 🚨 Extra ruta</p>
      ) : (
        <p className="text-xs text-gray-500 font-medium text-center">Regular</p>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total Solicitado',
      sortable: true,
      render: (row) => (
        <Badge variant="outline">{row.total_solicitado} unidades</Badge>
      )
    },
    {
      id: 'total_entregado',
      label: 'Total Entregado',
      sortable: true,
      render: (row) => (
        <Badge variant={row.total_entregado === row.total_solicitado ? 'default' : 'secondary'}>
          {row.total_entregado} unidades
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'En Proceso': 'bg-blue-100 text-blue-800',
          'Completada': 'bg-green-100 text-green-800',
          'Cancelada': 'bg-red-100 text-red-800',
          'No aprobada': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria-propiedad',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-purple-600 hover:bg-purple-50'
        },
        {
          key:'eliminar-solicitud',
          icon:'Trash',
          tooltip:'Eliminar Solicitud',
          className:'text-red-600 hover:bg-red-50'
        }
      ]
    }
  ];

  // Función para manejar acciones de la tabla
  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle-lenceria-propiedad') {
      setSelectedSolicitud(row);
      setIsDetalleModalOpen(true);
    }
    if (actionKey === 'eliminar-solicitud') {
      handleEliminarSolicitud(row);
    }
  };

  // Función para abrir modal de eliminar
  const handleEliminarSolicitud = (solicitud) => {
    setSolicitudToDelete(solicitud);
    setIsEliminarModalOpen(true);
  };

  // Función para confirmar eliminación
  const handleConfirmEliminar = async (solicitud, motivo) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/${solicitud._id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            motivo,
            eliminadoPor: {
              id: user._id || user.id,
              nombre: user.name || user.nombre
            }
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al eliminar la solicitud');
      }

      toast({
        title: 'Éxito',
        description: 'Solicitud eliminada correctamente',
      });

      // Recargar solicitudes
      fetchSolicitudes();
      
      // Cerrar modal
      setIsEliminarModalOpen(false);
      setSolicitudToDelete(null);
    } catch (error) {
      console.error('Error al eliminar solicitud:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar la solicitud',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Función para cerrar modal de detalle
  const handleCloseDetalle = () => {
    setIsDetalleModalOpen(false);
    setSelectedSolicitud(null);
  };

  const fetchSolicitudes = useCallback(async () => {
    setIsLoadingSolicitudes(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPage,
          query: { 
            'hospedaje.id': hospedajeId,
            estado: { $ne: 'Eliminado' } // Excluir eliminadas en el backend
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSolicitudes(data.data || []);
        setTotalItems(data.totalRecords || 0);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSolicitudes(false);
    }
  }, [currentPage, hospedajeId, toast]);

  useEffect(() => {
    loadHospedaje();
  }, [hospedajeId]);

  useEffect(() => {
    if (hospedajeId) {
      fetchSolicitudes();
    }
  }, [fetchSolicitudes]);

  // Calcular capacidad total de camas y establecer ocupación
  useEffect(() => {
    if (camasData.length > 0) {
      const capacidadTotal = camasData.reduce((total, cama) => {
        const capacidadCama = cama.detalles?.capacidad || 0;
        return total + (capacidadCama * cama.cantidad);
      }, 0);

      if (capacidadTotal > 0) {
        setOcupacion(capacidadTotal);
      }
    }
  }, [camasData]);

  // Función para inicializar prendas básicas
  const inicializarPrendasBasicas = useCallback(() => {
    const prendasBasicas = [];

    // Agregar prendas básicas de camas
    camasData.forEach(cama => {
      if (cama.detalles?.prendas) {
        cama.detalles.prendas.forEach(prenda => {
          if (prenda.basico === 'Si') {
            const key = `${cama.tipo}-${cama.camaId}-${prenda.nombre}`;
            // Calcular según si es PAX o no
            const totalUnidades = prenda.pax === 'Si' 
              ? prenda.cantidad * ocupacion 
              : prenda.cantidad * cama.cantidad;
            prendasBasicas.push({
              key,
              tipo: cama.tipo,
              origen: cama.nombre,
              prendaId: prenda.prendaId,
              nombre: prenda.nombre,
              peso: prenda.peso,
              cantidad: totalUnidades,
              cantidadBase: prenda.cantidad,
              itemCantidad: cama.cantidad,
              basico: 'Si',
              pax: prenda.pax || 'No'
            });
          }
        });
      }
    });

    // Agregar prendas básicas de baños
    banosData.forEach(bano => {
      if (bano.detalles?.prendas) {
        bano.detalles.prendas.forEach(prenda => {
          if (prenda.basico === 'Si') {
            const key = `${bano.tipo}-${bano.banoId}-${prenda.nombre}`;
            // Calcular según si es PAX o no
            const totalUnidades = prenda.pax === 'Si' 
              ? prenda.cantidad * ocupacion 
              : prenda.cantidad * bano.cantidad;
            prendasBasicas.push({
              key,
              tipo: bano.tipo,
              origen: bano.nombre,
              prendaId: prenda.prendaId,
              nombre: prenda.nombre,
              peso: prenda.peso,
              cantidad: totalUnidades,
              cantidadBase: prenda.cantidad,
              itemCantidad: bano.cantidad,
              basico: 'Si',
              pax: prenda.pax || 'No'
            });
          }
        });
      }

      // Agregar insumos de baños (siempre básicos, nunca PAX)
      if (bano.detalles?.insumos) {
        bano.detalles.insumos.forEach(insumo => {
          const key = `insumo-${bano.banoId}-${insumo.nombre}`;
          const totalUnidades = insumo.cantidad * bano.cantidad;
          prendasBasicas.push({
            key,
            tipo: 'insumo',
            tipo_item: 'insumo',
            insumoId: insumo.insumoId || null,
            origen: bano.nombre,
            nombre: insumo.nombre,
            peso: 0,
            cantidad: totalUnidades,
            cantidadBase: insumo.cantidad,
            itemCantidad: bano.cantidad,
            basico: 'Si',
            pax: 'No'
          });
        });
      }
    });

    return prendasBasicas;
  }, [camasData, banosData, ocupacion]);

  // Inicializar prendas básicas cuando se cargan las camas y baños
  useEffect(() => {
    if (camasData.length > 0 || banosData.length > 0) {
      const prendasBasicas = inicializarPrendasBasicas();
      if (prendasBasicas.length > 0) {
        setSelectedItems(prendasBasicas);
      }
    }
  }, [camasData, banosData, ocupacion, inicializarPrendasBasicas]);

  const loadHospedaje = async () => {
    setIsLoading(true);
    try {
      const response = await fetchHospedajeById(hospedajeId);
      if (response && response.data) {
        setHospedaje(response.data);
        await loadCamasYBanos(response.data);
      }
    } catch (error) {
      console.error('Error al cargar hospedaje:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la información del activo',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCamasYBanos = async (hospedajeData) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

      // Cargar detalles de camas parametrizadas
      if (hospedajeData.camas && hospedajeData.camas.length > 0) {
        const camasPromises = hospedajeData.camas.map(async (cama) => {
          try {
            const response = await fetch(`${API_URL}/camas/${cama.camaId}`);
            const data = await response.json();
            return {
              ...cama,
              detalles: data.data || data,
              tipo: 'cama'
            };
          } catch (error) {
            console.error(`Error al cargar cama ${cama.camaId}:`, error);
            return { ...cama, detalles: null, tipo: 'cama' };
          }
        });
        const camasConDetalles = await Promise.all(camasPromises);
        setCamasData(camasConDetalles);
      }

      // Cargar detalles de baños parametrizados
      if (hospedajeData.banos && hospedajeData.banos.length > 0) {
        const banosPromises = hospedajeData.banos.map(async (bano) => {
          try {
            const response = await fetch(`${API_URL}/banos/${bano.banoId}`);
            const data = await response.json();
            return {
              ...bano,
              detalles: data.data || data,
              tipo: 'bano'
            };
          } catch (error) {
            console.error(`Error al cargar baño ${bano.banoId}:`, error);
            return { ...bano, detalles: null, tipo: 'bano' };
          }
        });
        const banosConDetalles = await Promise.all(banosPromises);
        setBanosData(banosConDetalles);
      }
    } catch (error) {
      console.error('Error al cargar camas y baños:', error);
    }
  };

  const handleTogglePrenda = (item, prenda, checked) => {
    const key = `${item.tipo}-${item.tipo === 'cama' ? item.camaId : item.banoId}-${prenda.nombre}`;
    // Calcular total de unidades según si es PAX o no
    // Si es PAX: solo multiplicar por ocupación (no por cantidad de camas/baños)
    // Si NO es PAX: multiplicar por cantidad de prendas * cantidad de camas/baños
    const totalUnidades = prenda.pax === 'Si' 
      ? prenda.cantidad * ocupacion 
      : prenda.cantidad * item.cantidad;

    setSelectedItems(prev => {
      if (checked) {
        // Agregar o actualizar
        const existing = prev.find(i => i.key === key);
        if (existing) {
          return prev; // Ya existe, no hacer nada
        } else {
          return [...prev, {
            key,
            tipo: item.tipo,
            origen: item.nombre,
            prendaId: prenda.prendaId, // ID de la prenda parametrizada
            nombre: prenda.nombre,
            peso: prenda.peso,
            cantidad: totalUnidades,
            cantidadBase: prenda.cantidad,
            itemCantidad: item.cantidad,
            basico: prenda.basico || 'No',
            pax: prenda.pax || 'No'
          }];
        }
      } else {
        // Remover
        return prev.filter(i => i.key !== key);
      }
    });
  };

  const handleUpdatePrendaCantidad = (key, newCantidad) => {
    const cantidad = parseInt(newCantidad) || 0;
    if (cantidad <= 0) {
      return; // No permitir 0 o negativos
    }

    setSelectedItems(prev => prev.map(i =>
      i.key === key
        ? { ...i, cantidad }
        : i
    ));
  };

  const isPrendaSelected = (item, prenda) => {
    const key = `${item.tipo}-${item.tipo === 'cama' ? item.camaId : item.banoId}-${prenda.nombre}`;
    return selectedItems.some(i => i.key === key);
  };

  const getPrendaCantidad = (item, prenda) => {
    const key = `${item.tipo}-${item.tipo === 'cama' ? item.camaId : item.banoId}-${prenda.nombre}`;
    const found = selectedItems.find(i => i.key === key);
    if (found) {
      return found.cantidad;
    }
    // Calcular según si es PAX o no
    return prenda.pax === 'Si' 
      ? prenda.cantidad * ocupacion 
      : prenda.cantidad * item.cantidad;
  };

  const handleRemovePrenda = (key) => {
    setSelectedItems(prev => {
      const item = prev.find(i => i.key === key);
      if (item && item.cantidad > 1) {
        return prev.map(i =>
          i.key === key
            ? { ...i, cantidad: i.cantidad - 1 }
            : i
        );
      } else {
        return prev.filter(i => i.key !== key);
      }
    });
  };

  const handleUpdateCantidad = (key, newCantidad) => {
    const cantidad = parseInt(newCantidad) || 0;
    if (cantidad <= 0) {
      setSelectedItems(prev => prev.filter(i => i.key !== key));
    } else {
      setSelectedItems(prev => prev.map(i =>
        i.key === key
          ? { ...i, cantidad }
          : i
      ));
    }
  };

  const calcularTotales = () => {
    const totalPrendas = selectedItems.reduce((sum, item) => sum + item.cantidad, 0);
    const pesoTotal = selectedItems.reduce((sum, item) => sum + (item.peso * item.cantidad), 0);
    return { totalPrendas, pesoTotal };
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const summaryItems = useMemo(() => {
    const groups = new Map();

    selectedItems.forEach((item) => {
      const isInsumo = item.tipo_item === 'insumo' || item.tipo === 'insumo';
      const groupKey = isInsumo 
        ? `insumo-${item.nombre}` 
        : (item.prendaId || `${item.nombre}-${item.peso}`);

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          nombre: item.nombre,
          pesoUnitario: item.peso,
          cantidadTotal: 0,
          pesoTotal: 0,
          tipo_item: isInsumo ? 'insumo' : undefined,
          detalles: [],
        });
      }

      const group = groups.get(groupKey);
      group.cantidadTotal += item.cantidad;
      group.pesoTotal += item.peso * item.cantidad;
      group.detalles.push({
        key: item.key,
        origen: item.origen,
        cantidad: item.cantidad,
        peso: item.peso,
      });
    });

    return Array.from(groups.values()).sort((a, b) => {
      // Insumos al final
      if (a.tipo_item === 'insumo' && b.tipo_item !== 'insumo') return 1;
      if (a.tipo_item !== 'insumo' && b.tipo_item === 'insumo') return -1;
      return a.nombre.localeCompare(b.nombre);
    });
  }, [selectedItems]);

  const todayString = formatDateForInput(new Date());
  const maxDateString = formatDateForInput(addDays(new Date(), 5));
  const isEntregaHoy = fechaEntrega === todayString;

  const handleFechaEntregaChange = (value) => {
    if (!value) return;

    let clampedValue = value;

    if (value < todayString) {
      clampedValue = todayString;
    } else if (value > maxDateString) {
      clampedValue = maxDateString;
    }

    setFechaEntrega(clampedValue);
  };

  const handleRemoveGroup = (keys) => {
    setSelectedItems((prev) => prev.filter((item) => !keys.includes(item.key)));
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar al menos una prenda',
        variant: 'destructive',
      });
      return;
    }

    if (isEntregaHoy && !justificacionMismoDia.trim()) {
      toast({
        title: 'Justificación requerida',
        description: 'Debes justificar por qué solicitas la entrega para el mismo día.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      const { totalPrendas, pesoTotal } = calcularTotales();
      const estadoSolicitud = isEntregaHoy ? 'No aprobada' : 'Pendiente';
      // Ajustar fecha para zona horaria Colombia (UTC-5) - Sumar 5 horas
      const fechaEntregaISO = new Date(`${fechaEntrega}T05:00:00.000Z`).toISOString();
      const trimmedJustificacion = justificacionMismoDia.trim();
      const finalObservaciones = isEntregaHoy && trimmedJustificacion
        ? `Justificación solicitud mismo día: ${trimmedJustificacion}${observaciones ? `\n${observaciones}` : ''}`
        : observaciones;

      // Agrupar items por prendaId para evitar duplicados en la solicitud
      const itemsAgrupados = new Map();
      
      selectedItems.forEach((item) => {
        // Usar prefijo para insumos para que no se mezclen con prendas
        const isInsumo = item.tipo_item === 'insumo' || item.tipo === 'insumo';
        const groupKey = isInsumo 
          ? `insumo-${item.nombre}` 
          : (item.prendaId || `${item.nombre}-${item.peso}`);
        
        if (!itemsAgrupados.has(groupKey)) {
          itemsAgrupados.set(groupKey, {
            id: isInsumo ? (item.insumoId || groupKey) : (item.prendaId || groupKey),
            nombre: item.nombre,
            cantidad: 0,
            peso: item.peso,
            tipo_item: isInsumo ? 'insumo' : undefined,
            origenes: [] // Array para guardar todos los orígenes
          });
        }
        
        const grupo = itemsAgrupados.get(groupKey);
        grupo.cantidad += item.cantidad;
        grupo.origenes.push({
          nombre: item.origen,
          tipo: item.tipo,
          cantidad: item.cantidad
        });
      });

      // Convertir el Map a array y generar el campo origen consolidado
      const itemsFinales = Array.from(itemsAgrupados.values()).map((item) => {
        const baseItem = {
          id: item.id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          peso: item.peso,
          origen: item.origenes.map(o => `${o.nombre} (${o.cantidad})`).join(', '),
        };

        // Marcar insumos con tipo_item y tipo_origen
        if (item.tipo_item === 'insumo') {
          baseItem.tipo_item = 'insumo';
          baseItem.tipo_origen = 'insumo';
        } else if (!item.id.startsWith('temp-') && !item.id.startsWith('insumo-')) {
          baseItem.tipo_origen = 'multiple';
        } else {
          console.warn(`⚠️ Prenda sin ID: ${item.nombre} - No se podrá descontar del inventario`);
          baseItem.tipo_origen = item.origenes[0]?.tipo || 'desconocido';
        }

        return baseItem;
      });

      const solicitudData = {
        creator: {
          id: user.id,
          nombre: user.name
        },
        hospedaje: {
          id: hospedaje._id,
          nombre: hospedaje.nombrePropiedad
        },
        nombrePropiedad: hospedaje.nombrePropiedad,
        edificio: {
          id: hospedaje.edificio?.id || hospedaje.edificio?._id,
          nombre: hospedaje.edificio?.nombre,
          ciudad: hospedaje.edificio?.ciudad
        },
        total_solicitado: totalPrendas,
        peso_total: pesoTotal,
        total_entregado: 0,
        items: itemsFinales,
        observaciones: finalObservaciones,
        estado: estadoSolicitud,
        fechaEntrega: fechaEntregaISO,
        justificacionMismoDia: isEntregaHoy ? trimmedJustificacion : undefined
      };

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/solicitud-prendas/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(solicitudData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      toast({
        title: 'Solicitud Creada',
        description: 'La solicitud de lencería limpia ha sido registrada exitosamente',
      });

      // Limpiar el formulario
      setObservaciones('');
      setJustificacionMismoDia('');
      setFechaEntrega(getTomorrowDateString());

      // Reinicializar prendas básicas usando la función reutilizable
      const prendasBasicas = inicializarPrendasBasicas();
      setSelectedItems(prendasBasicas);

      // Recargar las solicitudes
      fetchSolicitudes();
    } catch (error) {
      console.error('Error al crear solicitud:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la solicitud',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (!hospedaje) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Activo no encontrado
          </h3>
          <p className="text-gray-500 mb-4">
            No se pudo encontrar la información del activo solicitado
          </p>
          <Button onClick={() => navigate(-1)}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  const { totalPrendas, pesoTotal } = calcularTotales();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Solicitar Lencería Limpia</h1>
            <p className="text-muted-foreground">{hospedaje.nombrePropiedad}</p>
          </div>
        </div>
      </div>

      {/* Información del Activo */}
      <Card>
        <CardHeader>
          <CardTitle>Información del Activo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Portafolio</p>
              <p className="font-semibold">{hospedaje.edificio?.nombre || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Coordinador</p>
              <p className="font-semibold">{hospedaje.coordinador?.nombre || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ciudad</p>
              <p className="font-semibold">{hospedaje.edificio?.ciudad || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground block mb-2">Ocupación (PAX)</label>
              <Input
                type="number"
                min="1"
                value={ocupacion}
                onChange={(e) => setOcupacion(parseInt(e.target.value) || 1)}
                className="h-10"
                placeholder="Número de personas"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
        {/* Resumen de solicitud */}
        <div className="order-1 lg:order-2 lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Resumen de Solicitud
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summaryItems.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No hay prendas seleccionadas
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {summaryItems.map((item) => {
                      const isInsumo = item.tipo_item === 'insumo';
                      return (
                        <div key={item.groupKey} className={`p-3 rounded-lg ${isInsumo ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200' : 'bg-gray-50 dark:bg-gray-800'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {isInsumo && <Droplets className="h-4 w-4 text-amber-600" />}
                                <p className="font-medium text-sm">{item.nombre}</p>
                                {isInsumo && (
                                  <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                                    Insumo
                                  </Badge>
                                )}
                              </div>
                              {!isInsumo && (
                                <p className="text-xs text-muted-foreground">
                                  Peso unitario: {item.pesoUnitario}g
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveGroup(item.detalles.map((detail) => detail.key))}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Cantidad:</span>
                            <span className="font-semibold">{item.cantidadTotal} unidades</span>
                          </div>
                          {!isInsumo && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Peso total:</span>
                              <span className="font-semibold">{Math.round(item.pesoTotal)} g</span>
                            </div>
                          )}
                          <div className="mt-3 space-y-1">
                            {item.detalles.map((detail) => (
                              <div key={detail.key} className="flex justify-between text-xs text-muted-foreground">
                                <span>{detail.origen}</span>
                                <span>{detail.cantidad} u</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tipos de prenda:</span>
                      <span className="font-semibold">{summaryItems.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total de unidades:</span>
                      <span className="font-semibold">{totalPrendas}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Peso total:</span>
                      <span className="text-primary">
                        {pesoTotal.toFixed(0)} g
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="fecha-entrega">Fecha de entrega/despacho. <span className="text-red-500">*</span></label>
                    <Input
                      id="fecha-entrega"
                      type="date"
                      min={todayString}
                      max={maxDateString}
                      value={fechaEntrega}
                      onChange={(e) => handleFechaEntregaChange(e.target.value)}
                      className="input-date-icon-dark"
                    />
                  </div>

                  {isEntregaHoy && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="justificacion-mismo-dia">
                        Justificación (requerida para entregas del mismo día) <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={justificacionMismoDia}
                        onValueChange={setJustificacionMismoDia}
                      >
                        <SelectTrigger id="justificacion-mismo-dia">
                          <SelectValue placeholder="Selecciona una justificación" />
                        </SelectTrigger>
                        <SelectContent>
                          {JUSTIFICACIONES_MISMO_DIA.map((justificacion, index) => (
                            <SelectItem key={index} value={justificacion}>
                              {justificacion}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Observaciones</label>
                    <Textarea
                      placeholder="Agrega observaciones adicionales..."
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={isSaving || selectedItems.length === 0}
                    className="w-full"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Creando solicitud...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Crear Solicitud
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Columna de Camas y Baños */}
        <div className="order-2 lg:order-1 lg:col-span-2 space-y-6">
          {/* Camas */}
          {camasData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bed className="h-5 w-5 text-purple-600" />
                  Camas del Activo
                </CardTitle>
                <CardDescription>
                  Selecciona las prendas de lencería por tipo de cama
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {camasData.map((cama, index) => (
                  <Card key={index} className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Bed className="h-6 w-6 text-purple-600" />
                        <div className="flex-1">
                          <h4 className="font-semibold">{cama.nombre}</h4>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span>
                              Cantidad: {cama.cantidad} {cama.cantidad === 1 ? 'unidad' : 'unidades'}
                            </span>
                            {cama.detalles?.capacidad && (
                              <span className="flex items-center gap-1">
                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                  👤 Capacidad: {cama.detalles.capacidad} {cama.detalles.capacidad === 1 ? 'persona' : 'personas'}
                                </Badge>
                                {cama.cantidad > 1 && (
                                  <span className="text-xs text-purple-600 font-medium">
                                    (Total: {cama.detalles.capacidad * cama.cantidad} PAX)
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {cama.detalles?.prendas && cama.detalles.prendas.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-purple-50 dark:bg-purple-950">
                                <TableHead className="w-12"></TableHead>
                                <TableHead>Prenda</TableHead>
                                <TableHead className="text-center">Por Cama</TableHead>
                                <TableHead className="text-center">Total</TableHead>
                                <TableHead className="text-center">Peso</TableHead>
                                <TableHead className="w-32">Cantidad</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {cama.detalles.prendas.map((prenda, pIndex) => {
                                const isSelected = isPrendaSelected(cama, prenda);
                                const key = `${cama.tipo}-${cama.camaId}-${prenda.nombre}`;
                                const cantidad = getPrendaCantidad(cama, prenda);

                                return (
                                  <TableRow key={pIndex} className={isSelected ? 'bg-purple-50/50 dark:bg-purple-950/50' : ''}>
                                    <TableCell>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={(checked) => handleTogglePrenda(cama, prenda, checked)}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{prenda.nombre}</span>
                                        {prenda.basico === 'Si' && (
                                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                            Básico
                                          </Badge>
                                        )}
                                        {prenda.pax === 'Si' && (
                                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                            PAX
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center">{prenda.cantidad}</TableCell>
                                    <TableCell className="text-center font-semibold text-purple-600">
                                      {prenda.pax === 'Si' ? prenda.cantidad * ocupacion : prenda.cantidad * cama.cantidad}
                                    </TableCell>
                                    <TableCell className="text-center">{prenda.peso}g</TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={cantidad}
                                        onChange={(e) => handleUpdatePrendaCantidad(key, e.target.value)}
                                        disabled={!isSelected}
                                        className="h-8 text-center"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          No hay prendas parametrizadas para este tipo de cama
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Baños */}
          {banosData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bath className="h-5 w-5 text-teal-600" />
                  Baños del Activo
                </CardTitle>
                <CardDescription>
                  Selecciona las prendas de lencería por tipo de baño
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {banosData.map((bano, index) => (
                  <Card key={index} className="border-l-4 border-l-teal-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Bath className="h-6 w-6 text-teal-600" />
                        <div>
                          <h4 className="font-semibold">{bano.nombre}</h4>
                          <p className="text-sm text-muted-foreground">
                            Cantidad: {bano.cantidad} {bano.cantidad === 1 ? 'unidad' : 'unidades'}
                          </p>
                        </div>
                      </div>

                      {bano.detalles?.prendas && bano.detalles.prendas.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-teal-50 dark:bg-teal-950">
                                <TableHead className="w-12"></TableHead>
                                <TableHead>Prenda</TableHead>
                                <TableHead className="text-center">Por Baño</TableHead>
                                <TableHead className="text-center">Total</TableHead>
                                <TableHead className="text-center">Peso</TableHead>
                                <TableHead className="w-32">Cantidad</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {bano.detalles.prendas.map((prenda, pIndex) => {
                                const isSelected = isPrendaSelected(bano, prenda);
                                const key = `${bano.tipo}-${bano.banoId}-${prenda.nombre}`;
                                const cantidad = getPrendaCantidad(bano, prenda);

                                return (
                                  <TableRow key={pIndex} className={isSelected ? 'bg-teal-50/50 dark:bg-teal-950/50' : ''}>
                                    <TableCell>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={(checked) => handleTogglePrenda(bano, prenda, checked)}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{prenda.nombre}</span>
                                        {prenda.basico === 'Si' && (
                                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                            Básico
                                          </Badge>
                                        )}
                                        {prenda.pax === 'Si' && (
                                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                            PAX
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center">{prenda.cantidad}</TableCell>
                                    <TableCell className="text-center font-semibold text-teal-600">
                                      {prenda.pax === 'Si' ? prenda.cantidad * ocupacion : prenda.cantidad * bano.cantidad}
                                    </TableCell>
                                    <TableCell className="text-center">{prenda.peso}g</TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={cantidad}
                                        onChange={(e) => handleUpdatePrendaCantidad(key, e.target.value)}
                                        disabled={!isSelected}
                                        className="h-8 text-center"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          No hay prendas parametrizadas para este tipo de baño
                        </p>
                      )}

                      {/* Insumos del baño */}
                      {bano.detalles?.insumos && bano.detalles.insumos.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Droplets className="h-4 w-4 text-amber-600" />
                            <span className="text-sm font-semibold text-amber-700">Insumos</span>
                          </div>
                          <div className="border border-amber-200 rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-amber-50 dark:bg-amber-950">
                                  <TableHead className="w-12"></TableHead>
                                  <TableHead>Insumo</TableHead>
                                  <TableHead className="text-center">Por Baño</TableHead>
                                  <TableHead className="text-center">Total</TableHead>
                                  <TableHead className="w-32">Cantidad</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {bano.detalles.insumos.map((insumo, iIndex) => {
                                  const insumoKey = `insumo-${bano.banoId}-${insumo.nombre}`;
                                  const isInsumoSelected = selectedItems.some(i => i.key === insumoKey);
                                  const insumoCantidad = selectedItems.find(i => i.key === insumoKey)?.cantidad || (insumo.cantidad * bano.cantidad);

                                  return (
                                    <TableRow key={iIndex} className={isInsumoSelected ? 'bg-amber-50/50 dark:bg-amber-950/50' : ''}>
                                      <TableCell>
                                        <Checkbox
                                          checked={isInsumoSelected}
                                          onCheckedChange={(checked) => {
                                            if (checked) {
                                              const totalUnidades = insumo.cantidad * bano.cantidad;
                                              setSelectedItems(prev => {
                                                const existing = prev.find(i => i.key === insumoKey);
                                                if (existing) return prev;
                                                return [...prev, {
                                                  key: insumoKey,
                                                  tipo: 'insumo',
                                                  tipo_item: 'insumo',
                                                  insumoId: insumo.insumoId || null,
                                                  origen: bano.nombre,
                                                  nombre: insumo.nombre,
                                                  peso: 0,
                                                  cantidad: totalUnidades,
                                                  cantidadBase: insumo.cantidad,
                                                  itemCantidad: bano.cantidad,
                                                  basico: 'Si',
                                                  pax: 'No'
                                                }];
                                              });
                                            } else {
                                              setSelectedItems(prev => prev.filter(i => i.key !== insumoKey));
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <Droplets className="h-4 w-4 text-amber-600" />
                                          <span className="font-medium">{insumo.nombre}</span>
                                          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                                            Insumo
                                          </Badge>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center">{insumo.cantidad}</TableCell>
                                      <TableCell className="text-center font-semibold text-amber-600">
                                        {insumo.cantidad * bano.cantidad}
                                      </TableCell>
                                      <TableCell>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={insumoCantidad}
                                          onChange={(e) => handleUpdatePrendaCantidad(insumoKey, e.target.value)}
                                          disabled={!isInsumoSelected}
                                          className="h-8 text-center"
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {camasData.length === 0 && banosData.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No hay camas ni baños configurados
                </h3>
                <p className="text-gray-500">
                  Debes configurar camas y baños parametrizados para este activo antes de crear solicitudes
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Tabla de Solicitudes */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-600" />
            Solicitudes de Lencería de esta Propiedad
          </CardTitle>
          <CardDescription>
            Historial de solicitudes realizadas para <span className="font-semibold">{hospedaje?.nombrePropiedad}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={solicitudes}
            isLoading={isLoadingSolicitudes}
            onAction={handleAction}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Modal de Detalle de Solicitud */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={handleCloseDetalle}
        solicitud={selectedSolicitud}
        tipo="lenceria"
      />

      {/* Modal de Eliminar Solicitud */}
      <EliminarSolicitudModal
        isOpen={isEliminarModalOpen}
        onClose={() => {
          setIsEliminarModalOpen(false);
          setSolicitudToDelete(null);
        }}
        solicitud={solicitudToDelete}
        onConfirm={handleConfirmEliminar}
        tipo="lenceria"
      />
    </motion.div>
  );
};

export default SolicitarLenceriaLimpia;
